import { Group, Stack } from "@mantine/core";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { spacing } from "#app/theme/v2/spacing";
import { IconButton } from "#components/atoms/IconButton/IconButton";
import { Tooltip } from "#components/atoms/Tooltip/Tooltip";
import { SelectDropdown } from "#components/molecules/Select/SelectDropdown";
import { QuoteRequestLineDto } from "#modules/quoteRequestLines/infra/fetchQuoteRequestLine.api";
import { QuoteRequestLinesTable } from "#modules/quoteRequestLinesV2/view/templates/QuoteRequestLinesTable";
import { useQuoteRequestLinesSort } from "#modules/quoteRequestLinesV2/view/useQuoteRequestLinesSort";
import { QuoteRequestDto } from "#modules/quoteRequests/infra/fetchQuoteRequest.api";
import { QuoteRequestCardSum } from "#modules/quoteRequestsV2/view/organisms/QuoteRequestCardSum";
import { QuoteRequestLinesSearchField } from "#modules/quoteRequestsV2/view/organisms/QuoteRequestLinesSearchField";
import { QuoteRequestNameHeader } from "#modules/quoteRequestsV2/view/organisms/QuoteRequestNameHeader";
import { useDebounce } from "#shared/helpers/useDebounce";
import { useUrlState } from "#shared/helpers/useUrlState";
import { Icon } from "#shared/icons/Icon";
import { ROUTES } from "#shared/routes";
import { sortQuoteRequestLines } from "shared";

import classes from "./QuoteRequestV2.view.module.css";

type QuoteRequestV2LoadingViewProps = {
  quoteRequest: null;
  isLoading: true;
};
type QuoteRequestV2LoadedViewProps = {
  quoteRequest: QuoteRequestDto;
  isLoading: false;
};
type QuoteRequestV2ViewProps = (QuoteRequestV2LoadingViewProps | QuoteRequestV2LoadedViewProps) & {
  quoteRequestLines: QuoteRequestLineDto[];
  isLinesInitialLoading: boolean;
};

export const QuoteRequestV2View = ({
  quoteRequest,
  isLoading,
  quoteRequestLines,
  isLinesInitialLoading: linesInitialLoading,
}: QuoteRequestV2ViewProps) => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const { batches } = sortQuoteRequestLines(quoteRequestLines);

  const [importModalOpened, setImportModalOpened] = useState(false);

  const { activeSort, applySort } = useQuoteRequestLinesSort(quoteRequest?.id, quoteRequestLines);
  const sortDirection =
    activeSort === "name-asc" ? "asc" : activeSort === "name-desc" ? "desc" : null;

  const sortOptions = [
    { value: "name-asc", label: t("quoteRequestV2.header.sortOptions.nameAsc") },
    { value: "name-desc", label: t("quoteRequestV2.header.sortOptions.nameDesc") },
    ...(activeSort === "custom"
      ? [{ value: "custom", label: t("quoteRequestV2.header.sortOptions.custom") }]
      : []),
  ];

  const handleSortChange = (value: string | null) => {
    if (value === "name-asc" || value === "name-desc") {
      applySort(value === "name-asc" ? "asc" : "desc");
    }
  };

  const handleHeaderSortToggle = () => {
    applySort(sortDirection === "asc" ? "desc" : "asc");
  };

  const [searchText, setSearchText] = useUrlState<string>("search", {
    initialValue: "",
  });
  const debouncedSearchText = useDebounce(searchText, 300);

  const handleGoBack = () => {
    navigate(ROUTES.v2QuoteRequests);
  };

  return (
    <Stack
      h="100%"
      gap={0}
      style={{ overflow: "hidden", marginLeft: `-${spacing[8]}`, paddingLeft: spacing[8] }}
    >
      <Stack gap={0} style={{ flexShrink: 0 }}>
        <Group
          justify="space-between"
          py={spacing[4]}
          gap={spacing[4]}
          className={classes.headerRow}
        >
          <QuoteRequestNameHeader
            onGoBack={handleGoBack}
            quoteRequestName={quoteRequest?.reference ?? undefined}
            clientName={quoteRequest?.client?.name ?? undefined}
            createdAt={quoteRequest?.createdAt}
            lineNumber={batches.size}
            isLoading={isLoading}
          />
          <QuoteRequestCardSum isLoading={isLoading} />
        </Group>

        <Group justify="flex-end" py={spacing[2]}>
          <Group gap={spacing[2]}>
            <QuoteRequestLinesSearchField value={searchText} onChange={setSearchText} />
            <Tooltip label={t("quoteRequestV2.header.sort")} position="bottom">
              <SelectDropdown
                label={t("quoteRequestV2.header.sortBy")}
                data={sortOptions}
                value={activeSort}
                onChange={handleSortChange}
                disabled={!quoteRequest}
                trigger={
                  <IconButton
                    variant="secondary"
                    size="sm"
                    aria-label={t("quoteRequestV2.header.sort")}
                  >
                    <Icon name="sort" size={16} />
                  </IconButton>
                }
              />
            </Tooltip>
            <Tooltip label={t("quoteRequestV2.header.addParts")} position="bottom">
              <IconButton
                variant="secondary"
                size="sm"
                aria-label={t("quoteRequestV2.header.addParts")}
                disabled={!quoteRequest}
                onClick={() => setImportModalOpened(true)}
              >
                <Icon name="plus" size={16} />
              </IconButton>
            </Tooltip>
          </Group>
        </Group>
      </Stack>
      <QuoteRequestLinesTable
        quoteRequest={quoteRequest}
        quoteRequestLines={quoteRequestLines}
        isInitialLoading={linesInitialLoading}
        importModalOpened={importModalOpened}
        onImportModalOpenedChange={setImportModalOpened}
        searchText={debouncedSearchText}
        sortDirection={sortDirection}
        onSortToggle={handleHeaderSortToggle}
      />
    </Stack>
  );
};
